import { foundation } from '../../../ui';
import {
  MODAL_COMPACT_MAX_HEIGHT,
  MODAL_LARGE_MAX_HEIGHT_MOBILE,
  modalFlexColumnShell,
  modalPinnedSection,
  modalBodyScrollRegion,
  overlayActionsRow,
  overlayPrimaryAction,
  overlaySecondaryAction,
} from '../../../ui/overlayStyles';

const { color, spacePx: sp, radii, shadow } = foundation;

/**
 * Same shell as the unscheduled-visit wizard: pinned header, stepper, scroll
 * body, pinned footer. One sizing rule for the whole small-wizard family
 * (founder standing order, 2026-09-27 — "the credits multi-step modal looks
 * bigger than the others"; RecordCreditModal.js / TransferCreditModal.js
 * adopted this same function that pass):
 *   - touch (phone + tablet): MODAL_LARGE_MAX_HEIGHT_MOBILE (unchanged)
 *   - web: MODAL_COMPACT_MAX_HEIGHT (was MODAL_LARGE_MAX_HEIGHT — every
 *     consumer of this function has a real internal scroll body
 *     (`wizardBodyStyle`'s `modalBodyScrollRegion`, rendered by
 *     the wizard shells / UnscheduledVisitFlow.js / the credits
 *     modals directly), so the 720px desktop ceiling can't clip content —
 *     see modalSizeTokens.js's own "verify a real internal scroll region,
 *     then switch" rule).
 */
export function wizardDialogStyle(isMobile) {
  return {
    ...modalFlexColumnShell,
    maxWidth: 480,
    width: 'calc(100vw - 32px)',
    maxHeight: isMobile ? MODAL_LARGE_MAX_HEIGHT_MOBILE : MODAL_COMPACT_MAX_HEIGHT,
    minHeight: 0,
    padding: 0,
    overflow: 'hidden',
    borderRadius: radii.xl,
    boxShadow: shadow.lg,
  };
}

export const wizardHeaderStyle = {
  ...modalPinnedSection,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: sp[3],
  // 12px top / right: the shared round close button (44px tap area, in the row) then sits 16px
  // from the top and right edge of the panel (KAN-948, OverlayCloseButton).
  padding: `${sp[3]} ${sp[3]} ${sp[3]} ${sp[5]}`,
  borderBottom: `1px solid ${color.neutral.border}`,
};

export const wizardHeaderTitleGroupStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: sp[3],
  minWidth: 0,
  flex: 1,
};

export function wizardBodyStyle(isMobile) {
  return {
    ...modalBodyScrollRegion,
    padding: isMobile ? `${sp[4]} ${sp[4]}` : `${sp[5]} ${sp[5]}`,
  };
}

// White, not surfaceMuted (founder standing order, 2026-09-28: "just do what
// the app does for modals — same UI"). This used to be the ONE grey footer
// strip in the app — every step wizard (route-transfer, unscheduled-visit,
// the credit modals, the manual-invoice/estimate builders) imports this same
// constant, so fixing the color HERE is the single source of truth; do not
// re-introduce a local white override in a consumer (WizardTotalBar.js and
// the estimate builder's old mobile footer both carried one before this fix —
// deleted).
export const wizardFooterStyle = {
  ...overlayActionsRow,
  flexShrink: 0,
  flexWrap: 'nowrap',
  marginTop: 0,
  padding: `${sp[3]} ${sp[4]} ${sp[4]}`,
  borderTop: `1px solid ${color.neutral.border}`,
  background: color.neutral.surfaceElevated,
};

export const wizardPrimaryButtonStyle = {
  ...overlayPrimaryAction,
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
};

export const wizardSecondaryButtonStyle = {
  ...overlaySecondaryAction,
  minHeight: 44,
};

export const summaryCardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: sp[3],
  padding: sp[4],
  background: color.neutral.surfaceMuted,
  border: `1px solid ${color.neutral.border}`,
  borderRadius: radii.lg,
};

export const summaryLabelStyle = {
  fontSize: foundation.fontSize.sm,
  fontWeight: foundation.fontWeight.semibold,
  color: color.neutral.textMuted,
  marginBottom: 2,
};

export const summaryValueStyle = {
  fontSize: foundation.fontSize.sm,
  fontWeight: foundation.fontWeight.semibold,
  color: color.neutral.text,
  overflowWrap: 'anywhere',
};
